import { ListingPreview } from '@mogzo/play';
import type { Listing, ListingImage, SubmissionView, SubmittedGameSetup } from '@mogzo/shared';
import { ordinal } from '@mogzo/shared';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useProject } from '../components/ProjectLayout';
import {
  checkName,
  checksSummary,
  Findings,
  Mark,
  OUTCOME_NAMES,
  StatusChip,
  took,
  versionState,
  when,
} from '../components/submissions';
import {
  Alert,
  BackLink,
  Button,
  buttonClass,
  cardClass,
  ErrorNotice,
  Loading,
} from '../components/ui';
import type { Project } from '../lib/api';
import { useDeveloper } from '../lib/developer';
import { formatBytes } from '../lib/format';
import { useApi } from '../lib/use-api';
import { withdraw } from './ProjectSubmissions';

interface Detail {
  submission: SubmissionView;
  projectId: string;
  listing: Listing;
  images: ListingImage[];
  gameSetup: SubmittedGameSetup;
}

const hint = 'text-[13px] text-muted';

function plural(n: number, one: string) {
  return `${n} ${one}${n === 1 ? '' : 's'}`;
}

/** What failed, check by check, with where and why: the developer's to-do list. */
function WhatToFix({ projectId, submission }: { projectId: string; submission: SubmissionView }) {
  const failed = submission.checks.filter((check) => check.outcome === 'failed');
  return (
    <section aria-labelledby="fix-heading" className={`${cardClass} p-6`}>
      <h2 id="fix-heading" className="text-xl font-semibold">
        What to fix
      </h2>
      <p className="mt-1.5 mb-2 text-muted">
        A reviewer only sees a version once it passes MOGZO’s checks.
      </p>
      {failed.map((check) => (
        <div key={check.id} className="flex gap-3 border-t border-line py-3.5">
          <Mark kind="failed" />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <strong>{checkName(check.id)}</strong>
            <Findings
              check={{ ...check, findings: check.findings.filter((f) => f.level === 'fail') }}
            />
            {check.id === 'listing' && (
              <Link
                to={`/projects/${projectId}/listing`}
                className="self-start text-sm font-semibold"
              >
                Open the store listing
              </Link>
            )}
          </div>
        </div>
      ))}
      <div className="mt-2 flex flex-wrap items-center gap-3 rounded-[6px] border border-line px-4 py-3.5">
        <span className="flex-[1_1_280px]">
          When they’re fixed, send a new version from your game’s folder. This one stays here as a
          record.
        </span>
        <span className="rounded-[10px] bg-field px-3 py-2 font-mono text-[13px]">
          mogzo submit
        </span>
      </div>
    </section>
  );
}

/** The reviewer's decision: what they asked for, why it was rejected, or that it's approved. */
function Decision({ submission }: { submission: SubmissionView }) {
  const review = submission.review;
  if (!review) return null;
  const { status } = submission;
  return (
    <section
      aria-labelledby="decision-heading"
      className={`${cardClass} flex flex-col gap-3.5 p-6`}
    >
      <h2 id="decision-heading" className="text-xl font-semibold">
        {status === 'approved'
          ? 'Approved'
          : status === 'rejected'
            ? 'Not approved'
            : 'What the reviewer asked for'}
      </h2>
      {status === 'approved' && (
        <p>
          {submission.release === 'live'
            ? 'It’s live.'
            : submission.release === 'replaced'
              ? 'It was live until a newer version replaced it.'
              : submission.release === 'rolled_back'
                ? 'It was live until MOGZO rolled the game back. Send a higher version to fix it.'
                : submission.release === 'skipped'
                  ? 'A newer version was approved before it went live.'
                  : submission.publishAt
                    ? `It goes live ${when(submission.publishAt, { at: true })}. Change or cancel that on the Submissions page.`
                    : 'Publish it on the Submissions page, now or at a set time.'}
        </p>
      )}
      {review.reason && (
        <p>
          <strong>Reason:</strong> {review.reason}
        </p>
      )}
      {review.problems.map((problem) => (
        <div
          key={problem.item}
          className="rounded-[6px] border border-transparent bg-warning-soft px-4 py-3"
        >
          <strong>{problem.item}</strong>
          <p className="mt-1 whitespace-pre-line">{problem.note}</p>
        </div>
      ))}
      {review.message && (
        <div>
          <div className="mb-1 text-xs font-semibold tracking-wide text-muted uppercase">
            Their message
          </div>
          <p className="whitespace-pre-line">{review.message}</p>
        </div>
      )}
      {status !== 'approved' && submission.kind === 'listing' && (
        <p className="rounded-[6px] border border-line px-4 py-3.5">
          {status === 'rejected'
            ? 'The live listing stays as it is. You can send a different change from the Store listing tab.'
            : 'Fix these on the Store listing tab, save, and send it again from there.'}
        </p>
      )}
      {status !== 'approved' && submission.kind === 'version' && (
        <div className="flex flex-wrap items-center gap-3 rounded-[6px] border border-line px-4 py-3.5">
          <span className="flex-[1_1_280px]">
            {status === 'rejected'
              ? `This closes ${submission.version}. Raise “version” in mogzo.json to send a new version when it’s ready.`
              : `Fix these, upload a new build or edit the store listing, and send it again from your game’s folder. The version can stay ${submission.version}, since it wasn’t approved.`}
          </span>
          <span className="rounded-[10px] bg-field px-3 py-2 font-mono text-[13px]">
            mogzo submit
          </span>
        </div>
      )}
    </section>
  );
}

function Report({
  project,
  detail,
  onReload,
}: {
  project: Project;
  detail: Detail;
  onReload: () => void;
}) {
  const me = useDeveloper();
  const [preview, setPreview] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const { submission, listing, gameSetup } = detail;
  const images = new Map(detail.images.map((image) => [image.id, image]));
  const open = submission.status === 'checking' || submission.status === 'in_review';
  const ran = took(submission.checksStartedAt, submission.checkedAt);
  // A store listing change sent on its own (M23): no build, source or checks of its own.
  const listingOnly = submission.kind === 'listing';

  const timeline: [string, string | null][] = [['Sent', submission.createdAt]];
  if (!listingOnly) {
    timeline.push([
      submission.status === 'checking'
        ? 'Being checked'
        : submission.status === 'failed_checks' && !submission.checkedAt
          ? 'Checks didn’t run'
          : submission.checks.some((c) => c.outcome === 'failed')
            ? 'Checks failed'
            : 'Checks passed',
      submission.checkedAt ??
        (submission.status === 'checking' ? submission.checksStartedAt : null),
    ]);
  }
  if (submission.status === 'withdrawn') timeline.push(['Withdrawn', submission.decidedAt]);
  if (submission.review) {
    timeline.push([
      submission.status === 'approved'
        ? 'Approved'
        : submission.status === 'rejected'
          ? 'Not approved'
          : 'Changes requested',
      submission.decidedAt,
    ]);
  }
  if (submission.publishedAt) timeline.push(['Went live', submission.publishedAt]);

  return (
    <>
      {preview && (
        <ListingPreview
          listing={listing}
          images={images}
          viewer={me.displayName}
          title={
            listingOnly
              ? 'Store listing change as sent'
              : `Store listing sent with version ${submission.version}`
          }
          note={
            listingOnly
              ? 'As it was when you sent it.'
              : 'As it was when you sent it. Changes since then go out with your next version.'
          }
          closeLabel="Back to the listing"
          onClose={() => setPreview(false)}
        />
      )}
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-2xl font-semibold">
              {listingOnly ? 'Store listing change' : `Version ${submission.version}`}
            </h2>
            <StatusChip status={versionState(submission)} />
          </div>
          <div className="flex flex-wrap gap-2">
            {open && (
              <Button
                variant="secondary"
                onClick={async () => {
                  const result = await withdraw(submission);
                  if (result === 'withdrawn') onReload();
                  else if (result) setNotice(result);
                }}
              >
                Withdraw
              </Button>
            )}
            <Link to={`/projects/${project.id}/submissions`} className={buttonClass('secondary')}>
              All submissions
            </Link>
          </div>
        </div>
        <p className="text-sm text-muted">
          {listingOnly
            ? `The game stays on ${submission.version}`
            : `Build #${submission.buildNumber}`}{' '}
          · sent {when(submission.createdAt, { at: true }).replace(/^Today, /, 'today at ')}
          {submission.sentFrom ? ` from ${submission.sentFrom}` : ''}
          {submission.checkedAt && !listingOnly
            ? ` · checked ${when(submission.checkedAt).replace(/^Today, .*/, 'today')}`
            : ''}
        </p>
      </div>
      {notice && <Alert tone="error">{notice}</Alert>}

      <div className="flex flex-wrap items-start gap-5">
        <div className="flex min-w-0 flex-[999_1_560px] flex-col gap-5">
          {submission.status === 'failed_checks' && (
            <WhatToFix projectId={project.id} submission={submission} />
          )}
          <Decision submission={submission} />
          {submission.status === 'in_review' && (
            <section className={`${cardClass} flex flex-col gap-1.5 p-6`}>
              <h2 className="text-xl font-semibold">In review</h2>
              <p className="text-muted">
                {submission.queuePosition
                  ? `${ordinal(submission.queuePosition)} in the queue. `
                  : ''}
                {listingOnly
                  ? 'A reviewer checks the listing and its content rating against the live one.'
                  : 'A reviewer plays it on a computer and a phone, reads the source and checks the listing.'}{' '}
                We’ll email you the decision.
              </p>
            </section>
          )}

          <section
            hidden={listingOnly}
            aria-labelledby="checks-heading"
            className={`${cardClass} p-6`}
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 id="checks-heading" className="text-xl font-semibold">
                Automatic checks
              </h2>
              {submission.checks.length > 0 && (
                <span className="text-sm font-semibold">{checksSummary(submission.checks)}</span>
              )}
            </div>
            {submission.status === 'checking' ? (
              <p className="mt-3 flex items-center gap-2.5 text-muted" aria-live="polite">
                <span
                  aria-hidden="true"
                  className="size-[18px] animate-spin rounded-full border-[3px] border-line-strong border-t-ink"
                />
                Checking the build and source. This takes a few minutes; this page updates by
                itself.
              </p>
            ) : (
              <p className={`${hint} mt-1.5 mb-2`}>
                {submission.checksStartedAt && submission.checkedAt
                  ? `Run ${when(submission.checksStartedAt, { at: true })}${ran ? `, ${ran}` : ''}, before a reviewer saw it.`
                  : 'The checks didn’t run.'}{' '}
                Warnings don’t stop a version: the reviewer looks at them.
              </p>
            )}
            {submission.checks.map((check) => (
              <div
                key={check.id}
                className="grid grid-cols-[22px_minmax(0,1fr)_auto] items-start gap-3 border-t border-line py-3"
              >
                <Mark kind={check.outcome} />
                <div className="flex min-w-0 flex-col gap-2">
                  <span>
                    <strong>{checkName(check.id)}</strong>
                    <span className={`${hint} block`}>{check.summary}</span>
                  </span>
                  <Findings check={check} />
                </div>
                <span className={hint}>{OUTCOME_NAMES[check.outcome]}</span>
              </div>
            ))}
          </section>
        </div>

        <aside className="flex max-w-[360px] min-w-0 flex-[1_1_280px] flex-col gap-4">
          <section aria-labelledby="sent-heading" className={`${cardClass} p-5`}>
            <h2 id="sent-heading" className="mb-3 text-[15px] font-semibold">
              What was sent
            </h2>
            <dl className="flex flex-col gap-3 text-sm">
              <div>
                <dt className={`${hint} font-semibold`}>Build</dt>
                <dd className="mt-0.5 font-semibold">
                  {listingOnly
                    ? `None: the game stays on ${submission.version} (build #${submission.buildNumber})`
                    : `#${submission.buildNumber}, version ${submission.version}`}
                </dd>
              </div>
              <div>
                <dt className={`${hint} font-semibold`}>Store listing</dt>
                <dd className="mt-0.5">
                  <button
                    type="button"
                    onClick={() => setPreview(true)}
                    className="font-semibold underline decoration-brand decoration-2 underline-offset-4"
                  >
                    See it as it was sent
                  </button>
                </dd>
              </div>
              <div hidden={listingOnly}>
                <dt className={`${hint} font-semibold`}>Source</dt>
                <dd className="mt-0.5 font-semibold break-all">
                  {submission.sourceDeleted ? (
                    <span className="font-normal text-muted">Deleted under the keeping rules</span>
                  ) : (
                    <>
                      <span className="font-mono text-[13px]">{submission.sourceName}</span> ·{' '}
                      {formatBytes(submission.sourceBytes)}
                    </>
                  )}
                </dd>
              </div>
              <div hidden={listingOnly}>
                <dt className={`${hint} font-semibold`}>Goes live with it</dt>
                <dd className="mt-0.5">
                  {gameSetup.leaderboards.length || gameSetup.achievements.length
                    ? `${plural(gameSetup.leaderboards.length, 'leaderboard')} and ${plural(gameSetup.achievements.length, 'achievement')}, as they were on the Data page`
                    : 'No leaderboards or achievements'}
                  {gameSetup.modes?.length
                    ? `; ${plural(gameSetup.modes.length, 'multiplayer mode')}, as they were on the Multiplayer page`
                    : ''}
                </dd>
              </div>
              {submission.notes && (
                <div>
                  <dt className={`${hint} font-semibold`}>Your note for the reviewer</dt>
                  <dd className="mt-0.5 whitespace-pre-line">{submission.notes}</dd>
                </div>
              )}
            </dl>
            <p hidden={listingOnly} className={`${hint} mt-3.5 leading-normal`}>
              Only reviewers open your source. MOGZO keeps it while this version could matter, and
              deletes it 90 days after the decision.
            </p>
          </section>

          <section aria-labelledby="timeline-heading" className={`${cardClass} p-5`}>
            <h2 id="timeline-heading" className="mb-3 text-[15px] font-semibold">
              Timeline
            </h2>
            <ol className="flex list-decimal flex-col gap-2.5 pl-[18px] text-sm">
              {timeline
                .filter(([, at]) => at)
                .map(([label, at]) => (
                  <li key={label}>
                    <strong>{label}</strong>
                    <span className={`${hint} block`}>{at && when(at, { at: true })}</span>
                  </li>
                ))}
            </ol>
          </section>
        </aside>
      </div>
    </>
  );
}

/** One submission's report (M21), under the Submissions tab. */
export function SubmissionDetail() {
  const { project, reload: reloadProject } = useProject();
  const { submissionId = '' } = useParams();
  const [reload, setReload] = useState(0);
  const detail = useApi<Detail>(`/v1/dev/submissions/${encodeURIComponent(submissionId)}`, reload);
  const checking = detail.status === 'ready' && detail.data.submission.status === 'checking';

  useEffect(() => {
    if (!checking) return;
    const timer = setInterval(() => setReload((n) => n + 1), 5000);
    return () => clearInterval(timer);
  }, [checking]);

  return (
    <>
      <BackLink to={`/projects/${project.id}/submissions`}>All submissions</BackLink>
      {detail.status === 'loading' && <Loading />}
      {detail.status === 'error' && <ErrorNotice error={detail.error} />}
      {detail.status === 'ready' && (
        <Report
          project={project}
          detail={detail.data}
          onReload={() => {
            setReload((n) => n + 1);
            reloadProject();
          }}
        />
      )}
    </>
  );
}
